import React, { useState, useEffect } from 'react';
import { ArrowUp, Clock, Music } from 'lucide-react';
import { C } from '../../styles/tokens';

type AssetType = 'sound-effect' | 'music';

interface AudioPromptBoxProps {
  type: AssetType;
  onGenerate: (prompt: string, settings: AudioAssetSettings) => Promise<void>;
  isGenerating?: boolean;
  /** Optional controlled prompt value/setter (e.g. so the parent can prefill
   * the textarea from a suggestion chip or a row's "regenerate" action). Falls
   * back to an internal useState when not provided, so this stays backward
   * compatible with any other consumer that doesn't pass these. */
  value?: string;
  onChange?: (value: string) => void;
}

interface AudioAssetSettings {
  duration: string;
  instrumental?: boolean;
}

const AudioPromptBox: React.FC<AudioPromptBoxProps> = ({ type, onGenerate, isGenerating = false, value, onChange }) => {
  const isControlled = value !== undefined;
  const [internalPrompt, setInternalPrompt] = useState('');
  const prompt = isControlled ? (value as string) : internalPrompt;
  const setPrompt = (v: string) => {
    if (onChange) onChange(v);
    if (!isControlled) setInternalPrompt(v);
  };
  const [showDurationDropdown, setShowDurationDropdown] = useState(false);
  const [showInstrumentalDropdown, setShowInstrumentalDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [instrumentalDropdownPosition, setInstrumentalDropdownPosition] = useState({ top: 0, left: 0 });
  const durationButtonRef = React.useRef<HTMLButtonElement>(null);
  const instrumentalButtonRef = React.useRef<HTMLButtonElement>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const autoResize = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  // Keep the textarea height in sync when the prompt is set programmatically
  // (e.g. a suggestion chip click or a row's "regenerate" action set the
  // controlled value from the parent).
  useEffect(() => {
    autoResize();
  }, [prompt]);


  const [settings, setSettings] = useState<AudioAssetSettings>({
    duration: 'auto',
    instrumental: false
  });

  const isMusic = type === 'music';
  const placeholder = isMusic 
    ? "Describe a song... (e.g., 'A lofi hiphop track with a chill vibe')"
    : "Describe a sound...";

  // Duration options based on type
  // Sound Effects: 1-30 seconds (CassetteAI)
  // Music: 5-240 seconds (ACE-Step)
  const durationOptions = isMusic 
    ? [
        { value: 'auto', label: 'Auto (60s)' },
        { value: '30', label: '30s' },
        { value: '60', label: '60s' },
        { value: '120', label: '2m' },
        { value: '180', label: '3m' },
        { value: '240', label: '4m' }
      ]
    : [
        { value: 'auto', label: 'Auto (30s)' },
        { value: '5', label: '5s' },
        { value: '10', label: '10s' },
        { value: '15', label: '15s' },
        { value: '20', label: '20s' },
        { value: '30', label: '30s' }
      ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (prompt.trim() && !isGenerating) {
      try {
        await onGenerate(prompt.trim(), settings);
        // Reset prompt after successful generation
        setPrompt('');
        if (textareaRef.current) textareaRef.current.style.height = 'auto';
      } catch (error) {
        // Error is handled by parent component
        console.error('Generation failed:', error);
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const getDurationLabel = () => {
    if (settings.duration === 'auto') {
      return isMusic ? 'Auto (60s)' : 'Auto (30s)';
    }
    const mins = parseInt(settings.duration) / 60;
    if (mins >= 1) return `${mins}m`;
    return `${settings.duration}s`;
  };

  return (
    <>
      <div
        style={{
          maxWidth: 760,
          margin: '0 auto',
        }}
      >
        <form onSubmit={handleSubmit}>
          {/* Prompt Box — static elevated card (no longer floats over content) */}
          <div
            style={{
              position: 'relative',
              zIndex: 8,
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              width: '100%',
              opacity: isGenerating ? 0.6 : 1,
              pointerEvents: isGenerating ? 'none' : undefined,
              transition: 'opacity .2s',
              background: C.w,
              boxShadow: '0 4px 20px rgba(15,23,42,.06)',
              border: `1px solid ${C.n200}`,
              borderRadius: '16px',
              overflow: 'hidden'
            }}
          >
            {/* Row 1: Text Input */}
            <div style={{ width: '100%', display: 'flex' }}>
              <textarea
                ref={textareaRef}
                value={prompt}
                onChange={(e) => { setPrompt(e.target.value); autoResize(); }}
                onKeyPress={handleKeyPress}
                placeholder={placeholder}
                maxLength={500}
                disabled={isGenerating}
                style={{
                  flex: 1,
                  padding: '16px 20px',
                  minHeight: '48px',
                  fontSize: '13px',
                  color: '#1f2937',
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  resize: 'none',
                  overflow: 'hidden',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            {/* Row 2: Controls (left) and Send Button (right) */}
            <div 
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '10px',
                width: '100%',
                paddingBottom: '10px',
                paddingLeft: '10px',
                paddingRight: '10px'
              }}
            >
              {/* Left: Duration Control + Instrumental (for music) */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '2px', paddingLeft: '2px' }}>
                {/* Duration Button */}
                <button
                  ref={durationButtonRef}
                  type="button"
                  disabled={isGenerating}
                  className="btn d-inline-flex align-items-center"
                  onClick={() => {
                    if (!showDurationDropdown && durationButtonRef.current) {
                      const rect = durationButtonRef.current.getBoundingClientRect();
                      setDropdownPosition({
                        top: rect.top - 8,
                        left: rect.left
                      });
                    }
                    setShowDurationDropdown(!showDurationDropdown);
                    setShowInstrumentalDropdown(false);
                  }}
                  style={{
                    background: C.n50,
                    border: 'none',
                    borderRadius: 999,
                    padding: '5px 11px',
                    fontSize: '13px',
                    color: C.n600,
                    fontWeight: 600,
                    transition: 'all 75ms',
                    display: 'inline-flex',
                    alignItems: 'center',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = C.n100;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = C.n50;
                  }}
                >
                  <Clock size={14} style={{ marginRight: '4px', flexShrink: 0 }} />
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {getDurationLabel()}
                  </span>
                </button>

                {/* Instrumental Button (Music only) */}
                {isMusic && (
                  <button
                    ref={instrumentalButtonRef}
                    type="button"
                    disabled={isGenerating}
                    className="btn d-inline-flex align-items-center"
                    onClick={() => {
                      if (!showInstrumentalDropdown && instrumentalButtonRef.current) {
                        const rect = instrumentalButtonRef.current.getBoundingClientRect();
                        setInstrumentalDropdownPosition({
                          top: rect.top - 8,
                          left: rect.left
                        });
                      }
                      setShowInstrumentalDropdown(!showInstrumentalDropdown);
                      setShowDurationDropdown(false);
                    }}
                    style={{
                      background: C.n50,
                      border: 'none',
                      borderRadius: 999,
                      padding: '5px 11px',
                      fontSize: '13px',
                      color: settings.instrumental ? C.p600 : C.n600,
                      fontWeight: 600,
                      transition: 'all 75ms',
                      display: 'inline-flex',
                      alignItems: 'center',
                      marginLeft: 6,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = C.n100;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = C.n50;
                    }}
                  >
                    <Music size={14} style={{ marginRight: '4px', flexShrink: 0 }} />
                    <span>
                      {settings.instrumental ? 'Instrumental' : 'With Vocals'}
                    </span>
                  </button>
                )}
              </div>

              {/* Right: Send Button */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {/* Generate/Send Button */}
                <button
                  type="submit"
                  disabled={!prompt.trim() || isGenerating}
                  style={{
                    position: 'relative',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    padding: 0,
                    background: prompt.trim() && !isGenerating ? C.n900 : C.n300,
                    border: 'none',
                    cursor: !prompt.trim() || isGenerating ? 'not-allowed' : 'pointer',
                    transition: 'all 75ms',
                    boxShadow: 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (prompt.trim() && !isGenerating) {
                      e.currentTarget.style.background = '#111827';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (prompt.trim() && !isGenerating) {
                      e.currentTarget.style.background = C.n900;
                    }
                  }}
                >
                  {isGenerating ? (
                    <div className="spinner-border spinner-border-sm" style={{ color: 'white', width: '16px', height: '16px' }} role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  ) : (
                    <ArrowUp size={16} color="white" strokeWidth={2.5} />
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>

      {/* Duration Dropdown */}
      {showDurationDropdown && (
        <>
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9999
            }}
            onClick={() => setShowDurationDropdown(false)}
          />
          <ul
            className="dropdown-menu show"
            style={{
              position: 'fixed',
              bottom: `calc(100vh - ${dropdownPosition.top}px)`,
              left: `${dropdownPosition.left}px`,
              minWidth: '120px',
              zIndex: 10000,
              transform: 'translateY(0)',
              marginBottom: '0',
              padding: '4px',
              borderRadius: '10px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 4px 16px rgba(0,0,0,.08)',
            }}
          >
            {durationOptions.map((option) => {
              const isSelected = settings.duration === option.value;
              return (
                <li key={option.value}>
                  <button
                    type="button"
                    className="dropdown-item"
                    onClick={() => {
                      setSettings({ ...settings, duration: option.value });
                      setShowDurationDropdown(false);
                    }}
                    style={{
                      fontSize: '13px',
                      fontWeight: isSelected ? 600 : 400,
                      color: isSelected ? '#1f2937' : '#374151',
                      background: isSelected ? '#f3f4f6' : 'transparent',
                      borderRadius: '6px',
                    }}
                  >
                    {option.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {/* Instrumental Dropdown (Music only) */}
      {isMusic && showInstrumentalDropdown && (
        <>
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9999
            }}
            onClick={() => setShowInstrumentalDropdown(false)}
          />
          <ul
            className="dropdown-menu show"
            style={{
              position: 'fixed',
              bottom: `calc(100vh - ${instrumentalDropdownPosition.top}px)`,
              left: `${instrumentalDropdownPosition.left}px`,
              minWidth: '140px',
              zIndex: 10000,
              transform: 'translateY(0)',
              marginBottom: '0',
              padding: '4px',
              borderRadius: '10px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 4px 16px rgba(0,0,0,.08)',
            }}
          >
            {[
              { label: 'With Vocals', value: false },
              { label: 'Instrumental', value: true },
            ].map((option) => {
              const isSelected = settings.instrumental === option.value;
              return (
                <li key={option.label}>
                  <button
                    type="button"
                    className="dropdown-item"
                    onClick={() => {
                      setSettings({ ...settings, instrumental: option.value });
                      setShowInstrumentalDropdown(false);
                    }}
                    style={{
                      fontSize: '13px',
                      fontWeight: isSelected ? 600 : 400,
                      color: isSelected ? '#1f2937' : '#374151',
                      background: isSelected ? '#f3f4f6' : 'transparent',
                      borderRadius: '6px',
                    }}
                  >
                    {option.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </>
  );
};

export default AudioPromptBox;
